<template>
  <div class="coupon-item">
    <div class="coupon-info">
      <div class="info-top">
        <div class="info-title">
          <div class="title-icon"></div>
          <div class="coupon-unit">{{ itemInfo.discount_amount ? '￥' : itemInfo.discount_rate ? '折' : '?' }}</div>
          <div class="coupon-type">{{ $t('AccountInfo.CouponItem.coupon') }}</div>
        </div>
        <div class="info-amount"
             :class="itemInfo.discount_amount && Math.floor(Number(itemInfo.discount_amount)).toString().length > 3 ? 'info-amount-smaller' : ''">
          {{
            itemInfo.discount_amount ?
              Math.floor(Number(itemInfo.discount_amount)) :
              itemInfo.discount_rate
                ? discountRate : '?' }}</div>
      </div>
      <div class="rule-title"
           @click="ruleClick">{{ $t('AccountInfo.CouponItem.rules') }} ></div>
      <div class="rule-content">{{
        itemInfo.threshold_amount && itemInfo.threshold_amount > 0 ?
          `${$t('AccountInfo.CouponItem.singleOrderThresholdPrefix')} ${Math.floor(itemInfo.threshold_amount)}
        ${$t('AccountInfo.CouponItem.singleOrderThresholdSuffix')}`
          : itemInfo.activity_type !== 'all' ?
            $t('AccountInfo.CouponItem.specifiedProducts') : $t('AccountInfo.CouponItem.allProducts')
      }} </div>
      <div class="usage-time">{{ dayjs(itemInfo.start_time).format("YYYY.MM.DD") }}-{{
        dayjs(itemInfo.end_time).format("YYYY.MM.DD") }}</div>
    </div>
    <div class="operation"
         :class="operationStatus !== 'unClaimed' ? '' : 'operation-unClaimed'"
         @click="receiveCoupon(itemInfo.id)">
      {{ operationStatus === "unClaimed" ? $t('AccountInfo.CouponItem.claimNow') : operationStatus === "claimed" ?
        $t('AccountInfo.CouponItem.claimed') : operationStatus ===
          "collected" ? $t('AccountInfo.CouponItem.collected') : operationStatus === "ended" ?
          $t('AccountInfo.CouponItem.ended') : "" }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { message } from "ant-design-vue"
import dayjs from "dayjs"
import { computed, ref, watch } from "vue"
import { useI18n } from 'vue-i18n'
import { claimCoupon } from "../api/coupon"
import type { SingleCoupon } from "../api/interface/couponInterface"

const props = defineProps<{ itemInfo: SingleCoupon }>()
const { t } = useI18n()
function removeLastZero(number: number) {
  let numberStr = number.toString()
  // 如果字符串以0结尾，移除最后一个字符
  if (numberStr.endsWith('0')) {
    numberStr = numberStr.slice(0, -1)
  }
  const result = parseInt(numberStr, 10)
  return result
}
const discountRate = computed(() => {
  let result = '?'
  const rate = props.itemInfo.discount_rate
  if (rate) {
    const countRate = removeLastZero(Math.floor(Number(rate) * 100))
    if (countRate > 0) result = String(countRate)
  }
  return result
})

const emits = defineEmits(['getCouponList', 'getMycouponList', 'openRuleModal'])

const receiveCoupon = async (id: number) => {
  const messageAlert = message
  try {
    if (operationStatus.value !== "unClaimed") return
    const res = await claimCoupon(id) as any
    if (res === t('AccountInfo.CouponItem.claimSuccess') || res === t('AccountInfo.CouponItem.alreadyOwned')) {
      operationStatus.value = "claimed"
      emits('getMycouponList')
    }
    message.success(res)
  } catch (e: any) {
    if (!(e?.message || e?.detail)) {
      messageAlert.error(t('AccountInfo.CouponItem.claimFailed'))
      return
    }
    console.log(e)
    const { message, detail, code } = e
    if (code === 403) {
      if (message === t('AccountInfo.CouponItem.alreadyOwned')) {
        operationStatus.value = "claimed"
        emits('getMycouponList')
      } else {
        operationStatus.value = "collected"
      }
      messageAlert.warning(message)
    } else if (code === 500) {
      operationStatus.value = "ended"
      messageAlert.warning(detail)
    }
  }
}
// unClaimed 未领取 claimed 已领取 collected 已领完  ended 已失效
const operationStatus = ref("unClaimed")
watch(() => props.itemInfo, (itemInfo) => {
  if (itemInfo.is_full) {
    operationStatus.value = "collected"
  }
}, { immediate: true, deep: true })

function ruleClick() {
  const info = {
    is_stackable: props.itemInfo.is_stackable,
    activity_scope: props.itemInfo.activity_scope,
    threshold_amount: props.itemInfo.threshold_amount
  }
  emits('openRuleModal', info)
}

</script>


<style lang="less" scoped>
.coupon-item {
  flex-shrink: 0;
  width: 280px;
  height: 294px;
  margin-right: 20px;
  background: linear-gradient(145deg, #FFF7F3, #FFFFFF);
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);

  .coupon-info {
    height: calc(100% - 68px);
    padding: 14px 20px 20px 40px;

    .info-top {
      display: flex;

      .info-title {
        padding-top: 36px;
        color: #111111;
      }

      .info-amount {
        margin-top: -6px;
        flex: 1;
        text-align: center;
        font-size: 84px;
        font-family: 'Din Bold', sans-serif;
        background: linear-gradient(to bottom right, #FF9000 0%, #DE4A09 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        box-sizing: border-box;
      }

      .info-amount-smaller {
        font-size: 60px;
        margin-top: 8px;
        margin-bottom: 24px;
      }

      .title-icon {
        width: 16px;
        height: 4px;
        background: #111111;
      }

      .coupon-unit {
        margin: 7px 0 -5px;
        font-size: 18px;
        font-weight: bold;
      }
    }

    .rule-title {
      cursor: pointer;
      color: #999999;
    }

    .rule-content {
      font-weight: bold;
      font-size: 16px;
      margin: 2px 0 2px;
    }

    .usage-time {
      color: #DE4A09;
    }
  }

  .operation {
    font-size: 20px;
    font-family: Microsoft YaHei;
    color: #FFFFFF;
    background: #cccccc;
    height: 68px;
    line-height: 68px;
    text-align: center;
  }

  .operation-unClaimed {
    cursor: pointer;
    background: #111111;
  }
}
</style>
